import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Upload, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type MissSample = { order: string | number; line: string | number; name: string; reason: string; metaKeys: string[] };
type Counts = { created: number; skipped: number; failed: number; miss_samples?: MissSample[]; failures_logged?: number };
const BATCH = 25;
const MAP_KEY = "wp_import_studio_map_v1";

export default function AdminWpImportUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<any[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [defaultStudioId, setDefaultStudioId] = useState("");
  const [studios, setStudios] = useState<{ id: string; name: string }[]>([]);
  const [yithNames, setYithNames] = useState<{ name: string; count: number }[]>([]);
  const [studioMap, setStudioMap] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem(MAP_KEY) ?? "{}"); } catch { return {}; }
  });
  const [importOrders, setImportOrders] = useState(true);
  const [importBookings, setImportBookings] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [phase, setPhase] = useState("");
  const [log, setLog] = useState<string[]>([]);
  const [totals, setTotals] = useState<Record<string, Counts>>({});
  const [missSamples, setMissSamples] = useState<MissSample[]>([]);
  const [failuresLogged, setFailuresLogged] = useState(0);

  useEffect(() => {
    supabase.from("studios").select("id,name").eq("active", true).order("display_order")
      .then(({ data }) => setStudios((data ?? []) as { id: string; name: string }[]));
  }, []);
  useEffect(() => {
    try { localStorage.setItem(MAP_KEY, JSON.stringify(studioMap)); } catch { /* ignore */ }
  }, [studioMap]);

  function pushLog(s: string) { setLog((p) => [...p, `${new Date().toLocaleTimeString()}  ${s}`]); }

  async function onFile(f: File) {
    setFile(f); setRows(null); setParseError(null);
    try {
      const text = await f.text();
      const parsed = JSON.parse(text);
      const arr = Array.isArray(parsed) ? parsed : parsed.orders ?? parsed.data ?? null;
      if (!Array.isArray(arr)) throw new Error("JSON root must be an array of WC orders (or { orders: [...] })");
      setRows(arr);
      // Extract unique bookable line-item names (anything with YITH-ish meta or just any line item)
      const counts = new Map<string, number>();
      for (const o of arr) {
        for (const li of (o.line_items ?? [])) {
          const meta = (li.meta_data ?? []) as Array<{ key?: string; display_key?: string }>;
          const looksBookable = meta.some((m) => {
            const k = String(m.display_key ?? m.key ?? "").toLowerCase();
            return k.includes("booking") || k.includes("from") || k.includes("to") ||
              k.includes("duration") || k.includes("persons");
          });
          if (!looksBookable) continue;
          const name = String(li.name ?? "").trim();
          if (!name) continue;
          counts.set(name, (counts.get(name) ?? 0) + 1);
        }
      }
      const names = [...counts.entries()].map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
      setYithNames(names);
      toast.success(`Parsed ${arr.length} orders`);
    } catch (e) {
      setParseError((e as Error).message);
    }
  }

  async function runEntity(entity: "orders" | "bookings", all: any[]) {
    const batches = Math.ceil(all.length / BATCH);
    setTotalBatches(batches); setProgress(0);
    setPhase(`Importing ${entity}…`);
    const agg: Counts = { created: 0, skipped: 0, failed: 0, failures_logged: 0 };
    const samples: MissSample[] = [];
    for (let i = 0; i < batches; i++) {
      const chunk = all.slice(i * BATCH, (i + 1) * BATCH);
      try {
        const { data, error } = await supabase.functions.invoke("wp-import-batch", {
          body: {
            entity,
            rows: chunk,
            default_studio_id: defaultStudioId || null,
            studio_map: studioMap,
          },
        });
        if (error) throw error;
        const c = data?.counts as Counts;
        agg.created += c.created; agg.skipped += c.skipped; agg.failed += c.failed;
        agg.failures_logged = (agg.failures_logged ?? 0) + (c.failures_logged ?? 0);
        if (c.miss_samples?.length) {
          for (const s of c.miss_samples) if (samples.length < 15) samples.push(s);
        }
        pushLog(`${entity} batch ${i + 1}/${batches}: +${c.created} created, ${c.skipped} skipped, ${c.failed} failed`);
      } catch (e) {
        agg.failed += chunk.length;
        pushLog(`${entity} batch ${i + 1}/${batches} ERROR: ${(e as Error).message}`);
      }
      setProgress(i + 1);
      setTotals((t) => ({ ...t, [entity]: { ...agg } }));
      if (entity === "bookings") {
        setMissSamples([...samples]);
        setFailuresLogged(agg.failures_logged ?? 0);
      }
    }
  }

  async function start() {
    if (!rows) return;
    if (importBookings && !defaultStudioId &&
        yithNames.some((y) => !studioMap[y.name.toLowerCase().trim()])) {
      const ok = confirm(
        "Some bookable products have no studio mapping and no default studio is set. " +
        "Those bookings will be logged as failures (not imported). Continue anyway?"
      );
      if (!ok) return;
    }
    setRunning(true); setLog([]); setTotals({}); setMissSamples([]); setFailuresLogged(0);
    try {
      if (importOrders) await runEntity("orders", rows);
      if (importBookings) await runEntity("bookings", rows);
      setPhase("Done");
      toast.success("Upload import finished");
    } finally {
      setRunning(false);
    }
  }

  const mappedCount = useMemo(
    () => yithNames.filter((y) => studioMap[y.name.toLowerCase().trim()] || defaultStudioId).length,
    [yithNames, studioMap, defaultStudioId],
  );
  const setMapping = (name: string, studioId: string) => {
    const k = name.toLowerCase().trim();
    setStudioMap((prev) => {
      const next = { ...prev };
      if (!studioId) delete next[k]; else next[k] = `studio:${studioId}`;
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <Link to="/admin/wp-import" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to WP Import
        </Link>

        <header>
          <h1 className="text-3xl font-extrabold uppercase tracking-tight">Upload-Based Import</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
            Edge function timeouts kill the live WP pull at ~150s. Export your WC orders to a single JSON file and upload it here.
            The browser will feed the file to the importer in batches of {BATCH}, surviving any timeout. Idempotent via <code>wp_id_map</code>.
          </p>
        </header>

        <Card className="p-6 space-y-4">
          <div>
            <Label>WooCommerce orders JSON</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Either a raw array <code>[{`{id, billing, line_items, ...}`}]</code> or <code>{`{ orders: [...] }`}</code>.
              Get it via <code>/wp-json/wc/v3/orders?per_page=100&page=N</code> (concat pages) or a WC export plugin.
            </p>
            <Input type="file" accept="application/json,.json"
              onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
            {parseError && <p className="text-destructive text-sm mt-2">{parseError}</p>}
            {rows && <p className="text-sm mt-2">✓ {rows.length} orders ready ({file?.name})</p>}
          </div>

          <div>
            <Label>Default studio (fallback when product name can't be matched)</Label>
            <Select value={defaultStudioId || "_none"} onValueChange={(v) => setDefaultStudioId(v === "_none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="— none —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— none —</SelectItem>
                {studios.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              {studios.length} active studio{studios.length === 1 ? "" : "s"} loaded.
              {studios.length === 0 && (
                <> <Link to="/admin/studios" className="underline">Add a studio first</Link>.</>
              )}
            </p>
          </div>

          {yithNames.length > 0 && (
            <div>
              <div className="flex items-center justify-between">
                <Label>Bookable products in this file</Label>
                <span className="text-xs text-muted-foreground">
                  {mappedCount}/{yithNames.length} mapped
                </span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Map each product to a studio. Unmapped items fall back to the default studio above
                or get logged as failures.
              </p>
              <div className="border border-border divide-y divide-border max-h-64 overflow-auto">
                {yithNames.map((y) => {
                  const k = y.name.toLowerCase().trim();
                  const raw = studioMap[k] ?? "";
                  const cur = raw.startsWith("studio:") ? raw.slice(7) : "";
                  return (
                    <div key={k} className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-xs">
                      <div className="col-span-7 truncate font-mono" title={y.name}>{y.name}</div>
                      <div className="col-span-1 text-right text-muted-foreground">{y.count}</div>
                      <div className="col-span-4">
                        <Select value={cur || "_none"} onValueChange={(v) => setMapping(y.name, v === "_none" ? "" : v)}>
                          <SelectTrigger className="h-8"><SelectValue placeholder="— pick studio —" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— use default / skip —</SelectItem>
                            {studios.map((s) => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={importOrders} onChange={(e) => setImportOrders(e.target.checked)} />
              Import orders
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={importBookings} onChange={(e) => setImportBookings(e.target.checked)} />
              Import bookings (YITH)
            </label>
          </div>

          <Button onClick={start} disabled={!rows || running || (!importOrders && !importBookings)}>
            {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {running ? phase : "Start Import"}
          </Button>
        </Card>

        {(running || log.length > 0) && (
          <Card className="p-6 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span>{phase || "Idle"}</span>
              <span className="text-muted-foreground">{progress}/{totalBatches} batches</span>
            </div>
            <div className="h-2 bg-muted overflow-hidden">
              <div className="h-full bg-primary transition-all"
                style={{ width: totalBatches ? `${(progress / totalBatches) * 100}%` : "0%" }} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {Object.entries(totals).map(([k, v]) => (
                <div key={k} className="border border-border p-3">
                  <div className="uppercase text-xs text-muted-foreground">{k}</div>
                  <div>+{v.created} created · {v.skipped} skipped · {v.failed} failed</div>
                  {k === "bookings" && (v.failures_logged ?? 0) > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {v.failures_logged} logged for manual review
                    </div>
                  )}
                </div>
              ))}
            </div>
            {(missSamples.length > 0 || failuresLogged > 0) && (
              <div className="border border-border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold uppercase tracking-wider">Booking parse misses</div>
                  <Link to="/admin/wp-import/failures" className="text-xs underline">
                    Review {failuresLogged} failures →
                  </Link>
                </div>
                {missSamples.length > 0 && (
                  <div className="text-xs font-mono space-y-1 max-h-48 overflow-auto">
                    {missSamples.map((s, i) => (
                      <div key={i} className="border-l-2 border-destructive/40 pl-2">
                        <div>#{s.order}/{s.line} <span className="text-muted-foreground">{s.name}</span> — <span className="text-destructive">{s.reason}</span></div>
                        <div className="text-muted-foreground truncate">keys: {s.metaKeys.join(", ")}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="bg-muted/40 p-3 max-h-96 overflow-auto font-mono text-xs space-y-1">
              {log.map((l, i) => <div key={i}>{l}</div>)}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}